import { supabase } from './supabase';

interface EmailData {
  templateKey: string;
  recipientEmail: string;
  variables?: Record<string, any>;
  /** Stable key for dedupe + cron recovery (e.g. welcome_email:{userId}). */
  idempotencyKey?: string;
}

interface EmailResponse {
  success: boolean;
  messageId?: string;
  message?: string;
  error?: string;
  deduped?: boolean;
}

/**
 * Send email using Supabase Edge Function with database-stored templates
 */
export async function sendEmail({
  templateKey,
  recipientEmail,
  variables = {},
  idempotencyKey,
}: EmailData): Promise<boolean> {
  try {
    const { data, error } = await supabase.functions.invoke('email', {
      body: {
        templateKey,
        recipientEmail,
        variables,
        ...(idempotencyKey ? { idempotencyKey } : {}),
      },
    });

    if (error) {
      console.error('Email function error:', error);
      throw new Error(`Failed to send email: ${error.message}`);
    }

    const response = data as EmailResponse;

    if (!response.success) {
      console.error('Email sending failed:', response.error);
      throw new Error(response.error || 'Failed to send email');
    }

    console.log(
      response.deduped
        ? 'Email already sent (idempotency):'
        : 'Email sent successfully:',
      response.messageId
    );
    return true;
  } catch (error) {
    console.error('Failed to send email:', error);

    // In development, don't throw errors to allow signup flow to continue
    if (process.env.NODE_ENV === 'development') {
      console.warn('Email sending failed in development, continuing...');
      return true;
    }

    throw error;
  }
}

/** Idempotency key so webhook/UI retries + email-retry-cron do not duplicate welcome. */
export function welcomeEmailIdempotencyKey(userId: string): string {
  return `welcome_email:${userId}`;
}

/**
 * Send welcome email to new user (Resend welcome-email expects storeURL only).
 */
export async function sendWelcomeEmail(
  email: string,
  _fullName: string,
  storeURL: string,
  userId?: string
): Promise<boolean> {
  return sendEmail({
    templateKey: 'welcome_email',
    recipientEmail: email,
    idempotencyKey: userId ? welcomeEmailIdempotencyKey(userId) : undefined,
    variables: {
      storeURL: storeURL,
    },
  });
}

/**
 * Confirm password was changed (Resend pw-change-successful: NAME, RESET_LINK).
 * RESET_LINK is used as a login CTA after the password is already updated.
 */
export async function sendPasswordChangeSuccessfulEmail(
  email: string,
  fullName: string,
  loginLink: string
): Promise<boolean> {
  return sendEmail({
    templateKey: 'pw_change_successful',
    recipientEmail: email,
    variables: {
      NAME: fullName || 'there',
      RESET_LINK: loginLink,
    },
  });
}

/**
 * Send delete account email to user
 */
export async function sendDeleteAccountEmail(email: string, fullName: string): Promise<boolean> {
  return sendEmail({
    templateKey: 'delete_account_email',
    recipientEmail: email,
    variables: {
      name: fullName,
    },
  });
}
